import { apiClient } from "@/api/api-client";
import ReactECharts from "echarts-for-react";
import { useEffect, useRef, useState } from "react";

const colors = {
	pending: "#f87171",
	withDecision: "#eab308",
	withAppeal: "#3b82f6",
	caseClosed: "#82ca9d",
};

const CasePerProvinceChart = () => {
	const refs = useRef<Array<ReactECharts | null>>([]);

	const staticOptions = {
		tooltip: {
			trigger: "axis",
			axisPointer: {
				type: "shadow",
			},
		},
		legend: {
			right: 0,
			top: 10,
		},
		grid: {
			left: "3%",
			right: "4%",
			bottom: "3%",
			containLabel: true,
		},
	};

	const [statistics, setStatistics] = useState<any>({});

	const fetchStatistics = async () => {
		const response = await apiClient.get("/backoffice/statistics-2");

		if (response && response.data) {
			const temp: any = [];

			response.data.map((item: any) => {
				temp.push({
					...staticOptions,
					title: {
						text: "",
						extra: {
							region_name: item.region_name,
							total_count: item.total_count,
							max_number: item.max_number,
						},
						top: 10,
						left: 0,
						textStyle: {
							fontSize: 14,
						},
					},
					xAxis: {
						type: "value",
						boundaryGap: [0, 0.01],
						minInterval: 1,
					},
					yAxis: {
						type: "category",
						inverse: true,
						data: item.provinces,
					},
					series: [
						{
							name: "Pending w/o Decision",
							type: "bar",
							data: item.pending_count,
							itemStyle: {
								color: colors.pending,
							},
						},
						{
							name: "Pending w/ Decision",
							type: "bar",
							data: item.with_decision_count,
							itemStyle: {
								color: colors.withDecision,
							},
						},
						{
							name: "With Appeal",
							type: "bar",
							data: item.with_appeal_count,
							itemStyle: {
								color: colors.withAppeal,
							},
						},
						{
							name: "Case Closed",
							type: "bar",
							data: item.case_closed_count,
							itemStyle: {
								color: colors.caseClosed,
							},
						},
					],
					graphic: [
						{
							type: "text",
							left: 5,
							bottom: 10,
							style: {
								text: "",
								fontSize: 14,
								backgroundColor: "none",
								// height: 34,
							},
							onclick: () => {},
						},
					],
					case_data: item.case_data,
				});

				setStatistics(temp);
			});
		}
	};

	const goBack = (index: number) => {
		refs.current[index]?.getEchartsInstance()?.setOption(statistics[index]);
	};

	const goToCaseCategory = (index: number, subIndex: number) => {
		const option = refs.current[index]?.getEchartsInstance().getOption() as {
			title: { text: string }[];
		};

		if (option?.title[0]?.text) {
			return;
		}

		refs.current[index]?.getEchartsInstance()?.setOption({
			...staticOptions,
			title: {
				text: statistics[index].yAxis.data[subIndex],
				top: 10,
				left: 0,
				textStyle: {
					fontSize: 14,
				},
			},
			legend: {
				right: 0,
				top: 10,
			},
			xAxis: {
				type: "value",
				boundaryGap: [0, 0.01],
				minInterval: 1,
			},
			yAxis: {
				type: "category",
				data: statistics[index].case_data[subIndex].cases,
			},
			series: [
				{
					name: "Pending w/o Decision",
					type: "bar",
					data: statistics[index].case_data[subIndex].pending_count,
					itemStyle: {
						color: colors.pending,
					},
				},
				{
					name: "Pending w/ Decision",
					type: "bar",
					data: statistics[index].case_data[subIndex].with_decision_count,
					itemStyle: {
						color: colors.withDecision,
					},
				},
			],
			graphic: [
				{
					type: "text",
					left: 5,
					bottom: 10,
					style: {
						text: "Back",
						fontSize: 14,
						// height: 34,
						fontWeight: "600",
						fill: "#fff",
						textVerticalAlign: "center",
						padding: [12, 12, 12, 12],
						borderRadius: 6,
						backgroundColor: "hsla(279, 42%, 45%)",
					},
					onclick: () => {
						goBack(index);
					},
				},
			],
		});
	};

	const computeChartHeight = (yAxisCount = 1) => {
		let height = 80;
		height = height + yAxisCount * 40;

		return `${height}px`;
	};

	useEffect(() => {
		fetchStatistics();
	}, []);

	return (
		<div className="grid grid-cols-12 w-full gap-4">
			{statistics &&
				statistics.length > 0 &&
				statistics.map((item: any, index: number) => (
					<div className="col-span-12" key={"region-" + index}>
						<div className="flex flex-col w-full gap-4">
							<div className="flex flex-col w-full h-auto rounded-md border border-border/50 overflow-hidden">
								<div className="flex flex-row w-full p-3 border-b border-border/50 items-center">
									<div className="flex flex-col w-6/12 text-base font-semibold text-foreground-dark">{item.title.extra.region_name}</div>
									<div className="flex text-right flex-col w-6/12 text-base font-semibold text-foreground-dark">
										Total Cases: {item.title.extra.total_count}
									</div>
								</div>

								<div className="w-full h-full shrink-0 px-2">
									<div
										style={{
											width: "100%",
											height: computeChartHeight(item.yAxis.data?.length),
										}}
									>
										<ReactECharts
											key={index}
											option={item}
											ref={(el) => (refs.current[index] = el)}
											style={{ height: "100%", width: "100%" }}
											onEvents={{
												click: (event: any) => {
													if (event.componentType !== "graphic") {
														goToCaseCategory(index, event.dataIndex);
													}
												},
											}}
										/>
									</div>
								</div>
							</div>
						</div>
					</div>
				))}
		</div>
	);
};

export default CasePerProvinceChart;
